import axios from 'axios';

// URL du proxy iCal via Caddy (évite les problèmes CORS)
const ICAL_URL = '/api/calendar';

// Interface pour les dates réservées
export interface BookedDate {
  start: Date;
  end: Date;
}

/**
 * Analyse manuelle d'un fichier iCal pour extraire les événements
 * @param icalData Données iCal brutes
 * @returns BookedDate[] Tableau des périodes réservées
 */
const parseICalData = (icalData: string): BookedDate[] => {
  console.log('Analyse manuelle des données iCal...');
  
  const bookedDates: BookedDate[] = [];
  const lines = icalData.split('\n');
  
  let inEvent = false;
  let startDate: Date | null = null;
  let endDate: Date | null = null;
  let summary: string | null = null;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Début d'un événement
    if (line === 'BEGIN:VEVENT') {
      inEvent = true;
      startDate = null;
      endDate = null;
      summary = null;
    }
    
    // Fin d'un événement
    else if (line === 'END:VEVENT') {
      if (inEvent && startDate && endDate) {
        console.log('Événement trouvé:', summary, 'du', startDate.toISOString(), 'au', endDate.toISOString());
        bookedDates.push({
          start: startDate,
          end: endDate
        });
      }
      inEvent = false;
    }
    
    // Date de début
    else if (inEvent && line.startsWith('DTSTART')) {
      const dateStr = line.split(':').pop() || '';
      startDate = parseICalDate(dateStr);
    }

    // Date de fin
    else if (inEvent && line.startsWith('DTEND')) {
      const dateStr = line.split(':').pop() || '';
      endDate = parseICalDate(dateStr);
    }
    
    // Résumé/titre de l'événement
    else if (inEvent && line.startsWith('SUMMARY')) {
      summary = line.substring(8);
    }
  }
  
  console.log('Nombre total de périodes réservées:', bookedDates.length);
  return bookedDates;
};

/**
 * Convertit une date iCal en objet Date
 * @param icalDate Date au format iCal (20250101T120000Z)
 * @returns Date
 */
const parseICalDate = (icalDate: string): Date => {
  // Format: YYYYMMDDTHHMMSSZ
  const year = parseInt(icalDate.substring(0, 4));
  const month = parseInt(icalDate.substring(4, 6)) - 1; // Les mois commencent à 0 en JavaScript
  const day = parseInt(icalDate.substring(6, 8));
  
  let hours = 0;
  let minutes = 0;
  let seconds = 0;
  
  // Si la date contient aussi l'heure
  if (icalDate.includes('T')) {
    const timeStr = icalDate.split('T')[1];
    hours = parseInt(timeStr.substring(0, 2));
    minutes = parseInt(timeStr.substring(2, 4));
    seconds = parseInt(timeStr.substring(4, 6));
  }
  
  return new Date(Date.UTC(year, month, day, hours, minutes, seconds));
};

/**
 * Récupère et analyse le fichier iCal d'Airbnb
 * @returns Promise<BookedDate[]> - Tableau des périodes réservées
 */
export const fetchBookedDates = async (): Promise<BookedDate[]> => {
  try {
    console.log('Récupération du calendrier Airbnb depuis:', ICAL_URL);
    
    // Récupération du fichier iCal via le proxy Caddy
    const response = await axios.get(ICAL_URL);
    const icalData = response.data;
    
    console.log('Données iCal reçues, longueur:', typeof icalData === 'string' ? icalData.length : 'non-string');
    
    // Analyse manuelle du fichier iCal
    const bookedDates = parseICalData(icalData);
    
    return bookedDates;
  } catch (error) {
    console.error('Erreur lors de la récupération du calendrier Airbnb:', error);
    // Retourner un tableau vide en cas d'erreur
    return [];
  }
};

/**
 * Récupère les dates réservées avec mise en cache
 * @returns Promise<BookedDate[]> - Tableau des périodes réservées
 */
let cachedBookedDates: BookedDate[] | null = null;
let lastFetchTime: number = 0;
const CACHE_DURATION = 3600000; // 1 heure en millisecondes

export const getBookedDates = async (): Promise<BookedDate[]> => {
  const currentTime = Date.now();
  
  // Si les données sont en cache et que le cache n'est pas expiré, retourner les données en cache
  if (cachedBookedDates && (currentTime - lastFetchTime < CACHE_DURATION)) {
    return cachedBookedDates;
  }
  
  // Sinon, récupérer les données fraîches
  const bookedDates = await fetchBookedDates();
  
  // Mettre à jour le cache
  cachedBookedDates = bookedDates;
  lastFetchTime = currentTime;
  
  return bookedDates;
};

// Dates de réservation statiques à utiliser en cas d'échec de récupération
export const fallbackBookedDates: BookedDate[] = [
  { start: new Date(2025, 6, 1), end: new Date(2025, 6, 15) },
  { start: new Date(2025, 7, 10), end: new Date(2025, 7, 20) },
  { start: new Date(2025, 8, 5), end: new Date(2025, 8, 15) },
];
